import { useFeatureAuth } from "@/services/backend/auth/hooks/use-feature-auth";
import {
  GetSamplingMetadataResponse,
  UpdateSamplingMetadata,
  useGetDeliveryByProcessQuery,
  useGetSamplingMetadataQuery,
  useUpdateSamplingMetadataMutation,
} from "@/services/backend/deliveries/delivery/service";
import { AirSamplerSelector } from "@/shared/components/domain/air-samplers/air-sampler-selector";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { useDebouncedMutationWithPersistenceStateContextUpdate } from "@/shared/lib/debounce/debounce";
import { skipToken } from "@reduxjs/toolkit/query";
import { ReactElement, useState } from "react";
import { useParams } from "react-router";

export function ActiveAirMicrobialIndexRoute(): ReactElement {
  const { processId } = useParams();
  const { data } = useGetDeliveryByProcessQuery({
    processId: processId as string,
  });
  const { data: samplingMetadata } = useGetSamplingMetadataQuery(
    data ? { deliveryId: data.id } : skipToken,
  );

  if (samplingMetadata == null || data == null) {
    return <div />;
  }

  return (
    <ActiveAirMicrobialMetadataCard
      id={data.id}
      samplingMetadata={samplingMetadata}
      immutable={data.immutable}
    />
  );
}

interface ActiveAirMicrobialMetadataCardProps {
  id: string;
  samplingMetadata: GetSamplingMetadataResponse;
  immutable?: boolean;
}

function ActiveAirMicrobialMetadataCard({
  id,
  samplingMetadata,
  immutable = false,
}: ActiveAirMicrobialMetadataCardProps): ReactElement {
  const canEdit = useFeatureAuth("inspections.delivery.edit");
  const [request, setRequest] = useState<UpdateSamplingMetadata>({
    id,
    activeAirMicrobialMetadata: samplingMetadata.activeAirMicrobialMetadata,
    samplingMetadata: samplingMetadata.samplingMetadata,
  });

  const [updateSamplingMetadata, { isLoading, error, isSuccess, reset }] =
    useUpdateSamplingMetadataMutation();
  useDebouncedMutationWithPersistenceStateContextUpdate(
    request,
    updateSamplingMetadata,
    isLoading,
    error,
    isSuccess,
    reset,
    0,
  );

  return (
    <div className="flex flex-col space-y-4">
      <Card className="shadow-none">
        <CardHeader>
          <CardTitle className="text-3xl">Probenahmedaten</CardTitle>
          <CardDescription>
            Die Daten der Proben für die gesamte Lieferung
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col space-y-4">
          <div className="flex w-full flex-col space-y-2">
            <Label htmlFor="samplingVolume">Probevolumen</Label>
            <Input
              disabled={!canEdit || immutable}
              id="samplingVolume"
              defaultValue={
                samplingMetadata?.activeAirMicrobialMetadata?.samplingVolume
              }
              onChange={(e) =>
                setRequest({
                  ...request,
                  activeAirMicrobialMetadata: {
                    ...request.activeAirMicrobialMetadata,
                    samplingVolume: Number(e.target.value),
                  },
                })
              }
              placeholder="Probevolumen"
              type="number"
              min={0}
            />
          </div>
        </CardContent>
      </Card>
      <Card className="shadow-none">
        <CardHeader>
          <CardTitle className="text-3xl">Luftkeimsammler</CardTitle>
          <CardDescription>
            Die Daten für den verwendeten Luftkeimsammler für diese Lieferung
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col space-y-4">
          <AirSamplerSelector
            disabled={!canEdit || immutable}
            preset={request.activeAirMicrobialMetadata.airSamplerOriginalId}
            onChange={(e) =>
              setRequest({
                ...request,
                activeAirMicrobialMetadata: {
                  ...request.activeAirMicrobialMetadata,
                  airSamplerHoleCount: Number(e.holeCount),
                  airSamplerDescription: e.description,
                  airSamplerOriginalId: e.originalId,
                },
              })
            }
          />
          <div className="flex w-full flex-col space-y-2">
            <Label htmlFor="description">Beschreibung</Label>
            <Input
              disabled
              id="description"
              defaultValue={
                samplingMetadata?.activeAirMicrobialMetadata
                  ?.airSamplerDescription
              }
              placeholder="Beschreibung"
              type="text"
            />
          </div>
          <div className="flex w-full flex-col space-y-2">
            <Label htmlFor="holeCount">Lochzahl</Label>
            <Input
              disabled
              defaultValue={
                samplingMetadata?.activeAirMicrobialMetadata
                  ?.airSamplerHoleCount
              }
              placeholder="Lochzahl"
              type="number"
              min={0}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
